// Sends the device's waiting changes to CRM, oldest first, one at a time.
// Order matters: a measure before its building, an area before its photos —
// so nothing goes before a record it points at that CRM doesn't have yet
// (send-order.ts, #614), and the first change that can't go stops the run, and
// the rest wait behind it. A request that never answers is given up at its
// deadline (send-deadline.ts), so it can't hold the queue.
//
// Runs on app open, when the connection comes back, when the app is brought
// back to the front, every minute while it's open, and from "Sync now".
// Safari has no background sync (docs/OFFLINE.md), so nothing runs minimised.
import { ApiError, getJobById, isConnectionFailure, removePhotoApi, savePhotoDirectionToApi, saveRecentJobToApi, saveRecordToApi, uploadPhotoToApi } from './api';
import { forSending, SCALE_FROM } from './condition';
import { deviceMatchesSite } from './device-crm';
import { forgetPhotoAndRow, getJob, getPhoto, isChange, listOutbox, patchFields, renamePhoto, type DeviceRecord, type DeviceTable, type OutboxEntry } from './device-db';
import { db } from './device-store';
import { adoptEtag, adoptEtagIfSynced, countAttempt, dropEntry, markRefused, markAttemptFailed, markJobSynced, markPhotoSent, markPhotoSentToRow, markRecordConflict, markRecordSent } from './device-sync';
import { renamedPhoto } from './photo-names';
import { isConflict, isStaleWrite, refusalReason } from './refusal';
import { isOwnSave } from './conflict-diff';
import { isTickTable } from './tick-rows';
import { updateParent } from './buildings';
import { dependenciesOf, sendOrder } from './send-order';
import { SendTimedOut, sendRequests, type SendRequests } from './send-deadline';

const SYNC_INTERVAL_MS = 60_000;
const HTTP_CONFLICT = 409;
const HTTP_NOT_FOUND = 404;
const HTTP_SERVER_ERROR = 500;
/** How many runs a note that a job was opened is tried for, when the server fails (5xx, a lost reply), before it's dropped. */
const MAX_OPEN_ATTEMPTS = 5;

export type SyncOutcome = 'done' | 'offline' | 'stopped';

let running: Promise<SyncOutcome> | null = null;

/**
 * Rows that are one fact each — a tick (tick-rows.ts), one REVIEW question's
 * answer (review-records.ts) — are always sent as a create, which CRM applies
 * over whatever is there (dvCreate): the later save wins, so two iPads, or a
 * copy loaded before someone else's answer, never leave one stuck in conflict.
 */
const sentAsCreate = (table: DeviceTable): boolean => isTickTable(table) || table === 'reviewanswer';

/**
 * A record to send, and for a rating its visit's scale as this device has it
 * (rating → visit area → visit; undefined for anything else), read in ONE
 * transaction: a scale change on this iPad (condition-scale.ts) clears the
 * rating and changes the scale in transactions of its own, so the two read
 * together are always both from before it or both from after. Read apart, the
 * old rating could go as made on the new scale (#525).
 */
async function recordToSend(id: string): Promise<{ record: DeviceRecord | undefined; visitScale: unknown }> {
  const tx = (await db()).transaction(['records'], 'readonly');
  const store = tx.objectStore('records');
  const record = await store.get(id);
  const visitArea = record?.table === 'observation' ? await store.get(record.parentId) : undefined;
  const visit = visitArea ? await store.get(visitArea.parentId) : undefined;
  await tx.done;
  return { record, visitScale: visit?.fields.hig_ratingscale };
}

/** CRM has the visit's scale change, its ratings cleared: the device's note of the old scale goes, unless the scale changed again meanwhile. */
async function forgetScaleFrom(visitId: string, sentScale: unknown): Promise<void> {
  const tx = (await db()).transaction(['records'], 'readwrite');
  const local = await tx.objectStore('records').get(visitId);
  if (local && local.fields.hig_ratingscale === sentScale) {
    const { [SCALE_FROM]: _sent, ...fields } = local.fields;
    await tx.objectStore('records').put({ ...local, fields });
  }
  await tx.done;
}

/**
 * A save refused as changed elsewhere (STALE_WRITE) after an earlier try got no
 * answer (given up at its deadline, or a lost reply): that try may have landed,
 * and the "someone else" is this device. When CRM's copy holds exactly what was
 * sent (the columns the save writes, as the API lists them), it was: CRM's etag is taken up and the change counts as sent, never a
 * conflict with itself. Anything else stays a conflict, as before.
 */
async function ownEarlierSave(entry: OutboxEntry, record: DeviceRecord, sentRevision: number, err: unknown, requests: SendRequests): Promise<boolean> {
  if (!entry.attempts || !isStaleWrite(err)) return false;
  const job = await requests.inTime(getJobById(record.jobId, requests.signal));
  const crm = job.records.find((r) => r.id === record.id);
  if (!crm || !isOwnSave(record.fields, crm.fields, job.writableColumns?.[record.table] ?? [])) return false;
  await markRecordSent(entry, sentRevision, crm.etag);
  return true;
}

async function sendRecord(entry: OutboxEntry, requests: SendRequests): Promise<void> {
  const { record, visitScale } = await recordToSend(entry.targetId);
  if (!record) return dropEntry(entry);
  const sentRevision = record.revision;
  try {
    // A rating goes with the scale it was made on, beside its fields (condition.ts forSending).
    const saved = await requests.inTime(
      saveRecordToApi(record.table, record.id, {
        ...forSending(record, visitScale),
        ...(record.etag && !sentAsCreate(record.table) ? { etag: record.etag, ...updateParent(record) } : { parentId: record.parentId }),
      }, requests.signal),
    );
    await markRecordSent(entry, sentRevision, saved.etag);
    if (typeof record.fields[SCALE_FROM] === 'string') await forgetScaleFrom(record.id, record.fields.hig_ratingscale);
    if (saved.measureId && saved.measureEtag) await adoptEtag(saved.measureId, saved.measureEtag);
    if (saved.versionId && saved.versionEtag) await adoptEtag(saved.versionId, saved.versionEtag);
    // Other areas whose photo folders this save renamed (a building renamed or added): their new etags, where nothing waits.
    for (const area of saved.areaEtags ?? []) await adoptEtagIfSynced(area.id, area.etag);
  } catch (err) {
    // Changed elsewhere, or an accepted option that is locked: keep the device's copy, flag it, stop sending it.
    // A frozen version (VERSION_FROZEN) is a refusal instead: set aside with its reason (refusal.ts). A
    // last-save-wins row that lost a race stays queued: the run stops and the next sync sends it again.
    if (isConflict(err, sentAsCreate(record.table))) {
      if (await ownEarlierSave(entry, record, sentRevision, err, requests)) return;
      return markRecordConflict(entry);
    }
    throw err;
  }
}

/** The area's photo names, from a NAME_TAKEN refusal. */
function namesInUse(err: ApiError): string[] {
  const names = (err.body as { names?: unknown } | undefined)?.names;
  return Array.isArray(names) ? names.filter((n): n is string => typeof n === 'string') : [];
}

async function sendPhoto(entry: OutboxEntry, requests: SendRequests, renamed = false): Promise<void> {
  const photo = await getPhoto(entry.targetId);
  // Gone, or already uploaded (its full-size copy is cleared then): nothing left to send.
  if (!photo || !photo.image) return dropEntry(entry);
  const direction = photo.direction ?? null;
  try {
    const saved = await requests.inTime(uploadPhotoToApi({
      areaId: photo.areaId,
      photoId: photo.id,
      surfaceId: photo.surfaceId ?? null,
      areaPhoto: !!photo.areaPhoto,
      fileName: photo.fileName,
      image: photo.image,
      direction,
      bearing: photo.bearing ?? null,
      position: photo.position ?? null,
    }, requests.signal));
    // Removed on another device while this waited: it goes from here too.
    if (saved.removed) return await forgetPhotoAndRow(photo.id);
    if (saved.on === 'row') return await markPhotoSentToRow(entry, saved.etag, saved.webUrl);
    // A photo with no row (taken on the older build): the API put it on the area's older list.
    const listed = saved.webUrl ? { photoId: photo.id, surfaceId: photo.surfaceId ?? undefined, name: photo.fileName, webUrl: saved.webUrl, ...(photo.areaPhoto ? { areaPhoto: true } : {}) } : undefined;
    await markPhotoSent(entry, saved.etag, direction, listed);
  } catch (err) {
    // Another device's photo of this area has the name: take the next free one (its row too) and send once more.
    if (!renamed && err instanceof ApiError && err.status === HTTP_CONFLICT && err.code === 'NAME_TAKEN') {
      const fileName = renamedPhoto(photo.fileName, namesInUse(err));
      await renamePhoto(photo, fileName);
      await patchFields(photo.id, { hig_name: fileName });
      return sendPhoto(entry, requests, true);
    }
    throw err;
  }
}

/** An uploaded photo's direction changed: update it on the area's photo list. */
async function sendPhotoMeta(entry: OutboxEntry, requests: SendRequests): Promise<void> {
  const photo = await getPhoto(entry.targetId);
  if (!photo) return dropEntry(entry);
  const direction = photo.direction ?? null;
  const saved = await requests.inTime(savePhotoDirectionToApi(photo.areaId, photo.id, direction, requests.signal));
  await markPhotoSent(entry, saved.etag, direction);
}

/** A photo removed on the device: CRM makes its row inactive and recycles its file. A row never in CRM has nothing to remove. */
async function sendPhotoRemove(entry: OutboxEntry, requests: SendRequests): Promise<void> {
  try {
    await requests.inTime(removePhotoApi(entry.targetId, requests.signal));
  } catch (err) {
    if (!(err instanceof ApiError && err.status === HTTP_NOT_FOUND)) throw err;
  }
  await forgetPhotoAndRow(entry.targetId);
}

/**
 * The job was opened: tell CRM, for the Recent jobs list on every device (#422).
 * A lost connection keeps it waiting; a server failure (5xx, a lost reply) keeps
 * it for the next run, MAX_OPEN_ATTEMPTS runs at most. Anything else drops it
 * with a note in the console: a missed open is never set aside, and never
 * blocks real changes queued behind it.
 */
async function sendOpened(entry: OutboxEntry, requests: SendRequests): Promise<void> {
  const sent = (await getJob(entry.targetId))?.lastOpenedAt;
  if (sent) {
    try {
      await requests.inTime(saveRecentJobToApi(entry.targetId, sent, requests.signal));
    } catch (err) {
      if (isConnectionFailure(err) || err instanceof SendTimedOut) throw err;
      if (err instanceof ApiError && err.status >= HTTP_SERVER_ERROR && entry.attempts + 1 < MAX_OPEN_ATTEMPTS) return countAttempt(entry);
      console.warn('Recent job not recorded', entry.targetId, err instanceof ApiError ? (err.body ?? err) : err);
    }
  }
  // Opened again while sending: keep it, so the newer time goes next run.
  if ((await getJob(entry.targetId))?.lastOpenedAt === sent) await dropEntry(entry);
}

const SENDERS: Record<OutboxEntry['kind'], (entry: OutboxEntry, requests: SendRequests) => Promise<void>> = {
  record: sendRecord,
  photo: (entry, requests) => sendPhoto(entry, requests),
  'photo-meta': sendPhotoMeta,
  'photo-remove': sendPhotoRemove,
  opened: sendOpened,
};

/** The outbox in the order to send it (send-order.ts), from the device's copies of the records and photos as they are now. */
async function waitingInSendOrder(): Promise<OutboxEntry[]> {
  const store = await db();
  const [entries, records, photos] = await Promise.all([listOutbox(), store.getAll('records'), store.getAll('photos')]);
  const recordById = new Map(records.map((r) => [r.id, r]));
  const photoById = new Map(photos.map((p) => [p.id, p]));
  return sendOrder(entries, (entry) => dependenciesOf(entry, (id) => recordById.get(id), (id) => photoById.get(id)));
}

async function runOnce(): Promise<SyncOutcome> {
  // Nothing goes until this device's jobs are known to be from this site's CRM (lib/device-crm.ts).
  if (!(await deviceMatchesSite())) return 'stopped';
  const syncedJobs = new Set<string>();
  for (const entry of await waitingInSendOrder()) {
    // Moved or dropped since this run read the list (a scale change sends the visit last: condition-scale.ts).
    if (!(await (await db()).get('outbox', entry.seq!))) continue;
    try {
      await SENDERS[entry.kind](entry, sendRequests());
      if (isChange(entry)) syncedJobs.add(entry.jobId);
    } catch (err) {
      if (isConnectionFailure(err)) return 'offline';
      // Refused (403, a 400 as sent), or no longer in CRM (404): set aside with the reason, and carry on with the rest (refusal.ts, refused.ts).
      const refused = refusalReason(err);
      if (refused) {
        await markRefused(entry, refused);
        continue;
      }
      // The whole reply (a proxy's error page, #387) goes to the console; the screen gets the short message.
      console.error('Sync stopped', entry.kind, entry.targetId, err instanceof ApiError ? (err.body ?? err) : err);
      await markAttemptFailed(entry, err instanceof Error ? err.message : String(err));
      return 'stopped';
    }
  }
  for (const jobId of syncedJobs) await markJobSynced(jobId);
  return 'done';
}

/** Send everything waiting. Concurrent calls share one run. */
export function syncNow(): Promise<SyncOutcome> {
  if (!navigator.onLine) return Promise.resolve('offline');
  running ??= runOnce().finally(() => {
    running = null;
  });
  return running;
}

/** Start the automatic triggers. Returns a function that stops them. */
export function startAutoSync(): () => void {
  const trigger = () => void syncNow();
  const onVisible = () => {
    if (document.visibilityState === 'visible') trigger();
  };
  window.addEventListener('online', trigger);
  document.addEventListener('visibilitychange', onVisible);
  const timer = window.setInterval(trigger, SYNC_INTERVAL_MS);
  trigger();
  return () => {
    window.removeEventListener('online', trigger);
    document.removeEventListener('visibilitychange', onVisible);
    window.clearInterval(timer);
  };
}
